// The things lab's share of the handle and the harness (gate C5): `__rpg.things` (the state, the chooser's answer,
// warps and scripted breaks, the slicer's per-slice and per-frame work, the walker, the navmesh), its invariants and
// its planted defects (`navOneSlice`, `interactIgnorePriority`, `gatherNeverRegrow`, by `?faults=` or `__rpg.faults`).
import { finite, type Invariant } from "@rpg/harness";
import { createInventory } from "@rpg/items";
import type { Runtime } from "@rpg/runtime";
import type { FaultGroup } from "../faults";
import type { Fight } from "../enemies/fight";
import type { ValleyRig } from "../valley/scene";
import { WALKER } from "./lab";
import { THINGS_FAULTS, type Things, type ThingsFaultName } from "./module";
import type { InteractPromptState } from "./prompt";
import type { ThingsView } from "./view";

export { thingsLab, type ThingsLabMount } from "./scene";
export { mountThingsView, type ThingsView } from "./view";
export { mountInteractPrompt, type InteractPromptState } from "./prompt";
export { THINGS_FAULTS, type Things } from "./module";

export const THINGS_FAULT_NAMES = Object.keys(THINGS_FAULTS) as ThingsFaultName[];

/** The things' planted defects as a group for `__rpg.faults` (each read by the module every step; `?faults=` names
 *  are read at load). */
export function thingsFaultGroup(search = typeof location !== "undefined" ? location.search : ""): FaultGroup {
  for (const n of new URLSearchParams(search).get("faults")?.split(",") ?? []) if ((THINGS_FAULT_NAMES as string[]).includes(n)) THINGS_FAULTS[n as ThingsFaultName] = true;
  return {
    names: THINGS_FAULT_NAMES,
    get: (n) => THINGS_FAULTS[n as ThingsFaultName] === true,
    set: (n, on) => void (THINGS_FAULTS[n as ThingsFaultName] = on),
  };
}

/** The things' invariants, after every step: the bag holds what it may, everything's numbers finite, a broken thing
 *  has neither its body nor its combat target, every fragment above the ground. */
export function thingsInvariants(things: () => Things, fight: () => Fight, terrainAt: (x: number, z: number) => number): Invariant[] {
  let inv: ReturnType<typeof createInventory> | null = null;
  return [
    () => {
      const t = things();
      inv ??= createInventory(t.content.items);
      const issues = inv.check(t.state().bag);
      return issues.length ? `bag: ${issues.join("; ")}` : null;
    },
    finite("things", () => things().state().pickups.list.flatMap((p) => [...p.pos, p.count])),
    () => {
      const t = things();
      const s = t.state();
      for (const id of Object.keys(s.broken)) {
        if (fight().collision.has(`thing:${id}`)) return `broken ${id} still has its body`;
        if (fight().combat.has(t.fighterOf(id))) return `broken ${id} is still a combat target`;
      }
      for (const fr of s.fragments) {
        if (!fight().collision.has(fr.body)) return `fragment ${fr.body} has no body`;
        const p = fight().collision.pose(fr.body).position;
        if (![p.x, p.y, p.z].every(Number.isFinite)) return `fragment ${fr.body} not finite`;
        if (p.y < terrainAt(p.x, p.z) - 0.5) return `fragment ${fr.body} below the ground`;
      }
      return null;
    },
  ];
}

/** The things' summary for `state()`. */
export function thingsSummary(t: Things) {
  const s = t.state();
  return {
    t: s.t,
    broken: Object.keys(s.broken).sort(),
    gathered: Object.entries(s.gather.nodes)
      .filter(([, n]) => n.collected)
      .map(([id]) => id)
      .sort(),
    // the bag the things fill (the rpg layer's inventory where the scene has one)
    bag: t.bag().stacks.map((x) => ({ ...x })),
    pickups: s.pickups.list.length,
    fragments: s.fragments.length,
    choice: s.interaction.current,
    acting: s.acting?.placement ?? null,
    reading: s.reading?.id ?? null,
    doors: { ...s.doors },
    levers: { ...s.levers },
    slicer: { busy: t.slicer.busy(), pending: t.slicer.pending().map((x) => [x.x, x.y]) },
  };
}

/** One frame's tile work: the slices that ran in its steps. */
export interface FrameWork {
  frame: number;
  steps: number;
  slices: number;
  ms: number;
}

/** The handle keys of the things lab (`__rpg.things`). */
export function thingsHandle(d: { things: () => Things; fight: () => Fight; rig: () => ValleyRig; runtime: Runtime; view: () => ThingsView | null; prompt: () => InteractPromptState | null }) {
  const { things, fight, rig, runtime } = d;
  // per frame: the slices its steps ran (the gate's "no frame over 2 ms of extra work")
  const frames: FrameWork[] = [];
  let seen = 0;
  let frame = 0;
  runtime.on(
    "late",
    () => {
      frame++;
      const sl = things().slices;
      const fresh = sl.filter((x) => x.step > seen);
      if (sl.length) seen = Math.max(seen, sl[sl.length - 1]!.step);
      if (!fresh.length) return;
      frames.push({ frame, steps: new Set(fresh.map((x) => x.step)).size, slices: fresh.length, ms: fresh.reduce((n, x) => n + x.ms, 0) });
      if (frames.length > 2000) frames.splice(0, frames.length - 1500);
    },
    { order: 60, name: "things: frame work" },
  );
  return {
    state: () => thingsSummary(things()),
    /** The whole state (a copy). */
    full: () => structuredClone(things().state()),
    /** Every placement: kind, piece, where its feet are, its yaw. */
    placements: () => things().content.placements.map((p) => ({ id: p.id, kind: p.kind, piece: p.piece, at: things().at(p.id), yaw: p.yaw })),
    /** Teleport the hero to (x, z) facing `yaw` (the ground gives y). */
    warp: (x: number, z: number, yaw?: number) => rig().warpHero(x, rig().terrainAt(x, z), z, yaw),
    /** The chooser's last answer: the chosen id and verb, every valid candidate scored, and what was rejected and why. */
    choice: () => structuredClone(things().choice()),
    /** Fire the interaction on the next step (as an interact press would). */
    press: () => void (things().press = true),
    /** The prompt as drawn (null before it is mounted). */
    prompt: () => d.prompt(),
    /** Break a breakable on the next step as a blow along `dir` would. */
    breakNow: (id: string, dir?: [number, number, number], severity?: number) => things().breakNow(id, dir, severity),
    /** Throw a rock (a strong impact). */
    throwRock: (from: [number, number, number], velocity: [number, number, number]) => things().throwRock(from, velocity),
    /** The hero swings (its weapon's first move) on the next step. */
    attack: () => void (fight().heroIntent = { kind: "attack" }),
    /** Where a placement's feet are. */
    at: (id: string) => things().at(id),
    /** A breakable's fighter id (its combat target). */
    fighterOf: (id: string) => things().fighterOf(id),
    /** The last `n` things events (gathered, regrown, broken, collected, fired, settled, faded, swapped). */
    events: (n = 200) => things().events.slice(-n),
    /** The combat events the fight logged (the hits on breakables are there). */
    combatEvents: (n = 200) => fight().events.slice(-n),
    /** The tile slicer's work: its stats, every slice (step, ms, units, stages), and the work per frame. */
    slicer: () => ({ stats: things().slicer.stats(), slices: things().slices.map((x) => ({ ...x })), frames: frames.map((x) => ({ ...x })), busy: things().slicer.busy(), pending: things().slicer.pending() }),
    /** Queue tiles for a rebuild with the shapes they have now (the same work again: the specs time a rebuild three
     *  times and take each slice's least, the test machine being shared). */
    rebuildTiles: (tiles: { x: number; y: number }[]) => things().slicer.request(tiles),
    /** Forget the slices and frames measured so far. */
    resetSlicer: () => {
      things().slices.length = 0;
      frames.length = 0;
      things().slicer.stats(true);
    },
    /** The walker (a crowd agent heading through the pass). */
    walker: () => {
      const a = fight().crowd.agent(WALKER.id);
      return a ? { pos: [...a.pos], vel: [...a.vel], goal: structuredClone(a.goal), arrived: a.arrived } : null;
    },
    /** The navmesh: a path, its report, a tile's dynamic shapes. */
    nav: {
      path: (a: [number, number, number], b: [number, number, number]) => things().nav.path(a, b),
      report: () => things().nav.report(),
      tileAt: (x: number, z: number) => things().nav.tileAt(x, z),
      builtWith: (x: number, y: number) => things().nav.builtWith({ x, y }),
      dynamic: () => things().nav.dynamicState(),
    },
    /** What the view draws. */
    view: () => d.view()?.report() ?? null,
  };
}
